import { Pill } from '../../ui/DataBits';
import { useI18n } from '../../i18n';
import type { PaperworkStatus, SchoolState, SchoolType } from './api';
import type { RoleGrantRow } from './api';

// Status pills. The word carries the meaning; colour is a second cue only
// (DESIGN_GUIDELINES.md §7, Pill). One vocabulary everywhere (spec,
// "Status vocabulary").
type SchoolLike = { school_type?: SchoolType; state?: SchoolState; paperwork_status?: PaperworkStatus };

export function TypePill({ school }: { school: SchoolLike }) {
  const { t } = useI18n();
  return <Pill kind="neutral">{school.school_type === 'customer' ? t['school.customer'] : t['school.sandbox']}</Pill>;
}

export function StatePill({ school }: { school: SchoolLike }) {
  const { t } = useI18n();
  switch (school.state) {
    case 'setting_up':
      return <Pill kind="warn">{t['school.settingUp']}</Pill>;
    case 'suspended':
      return <Pill kind="crit">{t['school.suspended']}</Pill>;
    case 'offboarded':
      return <Pill kind="neutral">{t['school.offboarded']}</Pill>;
    default:
      return <Pill kind="good">{t['school.live']}</Pill>;
  }
}

export function PaperworkPill({ school }: { school: SchoolLike }) {
  const { t } = useI18n();
  if (!school.paperwork_status || school.state === 'offboarded') return null;
  const doc = school.school_type === 'customer' ? t['pa.dpaShort'] : t['pa.agreementShort'];
  return school.paperwork_status === 'signed' ? (
    <Pill kind="good">{t['pa.paperworkPill'](doc, t['pa.pwSigned'])}</Pill>
  ) : (
    <Pill kind="warn">
      {t['pa.paperworkPill'](doc, school.paperwork_status === 'sent' ? t['pa.pwSent'] : t['pa.pwNotStarted'])}
    </Pill>
  );
}

export function SchoolPills({ school }: { school: SchoolLike }) {
  return (
    <>
      <TypePill school={school} />
      <StatePill school={school} />
      <PaperworkPill school={school} />
    </>
  );
}

export function SetupProgress({ done, total }: { done: number; total: number }) {
  const { t } = useI18n();
  return (
    <span className="setup-progress">
      <span className="track" aria-hidden="true">
        <i style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
      </span>
      <span>{t['pa.setupProgress'](done, total)}</span>
    </span>
  );
}

export function RolePill({ grant }: { grant: Pick<RoleGrantRow, 'role' | 'is_primary' | 'status'> }) {
  const { t } = useI18n();
  const name =
    grant.role === 'head_of_school'
      ? grant.is_primary
        ? t['pa.rolePrimaryHead']
        : t['pa.roleDeputyHead']
      : grant.role === 'school_admin'
        ? t['role.schoolAdmin']
        : grant.role === 'counsellor'
          ? t['role.counsellor']
          : t['role.teacher'];
  return grant.status === 'pending_approval' ? (
    <Pill kind="warn">{t['pa.rolePending'](name)}</Pill>
  ) : (
    <Pill kind="neutral">{name}</Pill>
  );
}

export function PersonStatusPill({ status, isTest, expired }: { status: string; isTest: boolean; expired: boolean }) {
  const { t } = useI18n();
  if (isTest && expired) return <Pill kind="crit">{t['pa.statusExpired']}</Pill>;
  if (status === 'deactivated') return <Pill kind="neutral">{t['pa.statusDeactivated']}</Pill>;
  return <Pill kind="good">{isTest ? t['pa.statusActiveTest'] : t['pa.statusActive']}</Pill>;
}
